import { axiosInstance } from '../../../api/axiosConfig';
import { downloadBlobResponse } from '@/utils/fileDownload';

/**
 * Client API outil-pro pour les invoices réparateur (vue vendeur).
 * Cf SPECS/repairer_payment/README.md Phase 4.
 */
export interface RepairerInvoiceListItem {
  id: string;
  savId: number;
  status: string;
  amountTtc: number;
  invoiceNumber: string;
  invoiceDate: string;
  sentToGarantAt: string;
  paidByGarantAt: string | null;
  paymentReferenceByGarant: string | null;
  paymentReferenceSuffix: string | null;
  /** true si le document facture (PDF ou image) est téléchargeable via downloadDocument. */
  hasDocument?: boolean;
  iban: string | null;
  bic: string | null;
  accountHolder: string | null;
}

export const repairerInvoiceApi = {
  /**
   * Liste les invoices du garant connecté, filtrées par statut.
   * `savId` optionnel restreint à un SAV (encart détail SAV).
   */
  list: async (
    statuses?: string[],
    savId?: number,
  ): Promise<RepairerInvoiceListItem[]> => {
    const params = new URLSearchParams();
    if (statuses?.length) params.set('status', statuses.join(','));
    if (savId) params.set('savId', String(savId));
    const qs = params.toString();
    const { data } = await axiosInstance.get<{ invoices: RepairerInvoiceListItem[] }>(
      `/pro-v2/sav/repairer-invoices${qs ? `?${qs}` : ''}`,
    );
    return data.invoices;
  },

  /**
   * Compte les factures à régler (badge sidebar). COUNT SQL dédié côté API
   * (pas de plafond, pas d'hydratation). 0 pour un vendeur hors cycle.
   */
  countToPay: async (): Promise<number> => {
    const { data } = await axiosInstance.get<{ count: number }>(
      '/pro-v2/sav/repairer-invoices/count?status=SENT_TO_GARANT',
    );
    return data.count;
  },

  /**
   * Déclare le paiement effectué pour une invoice.
   */
  markPaid: async (
    invoiceId: string,
    paymentDate: string,
    paymentReference?: string,
  ): Promise<{ status: string }> => {
    const { data } = await axiosInstance.post<{ status: string }>(
      `/pro-v2/sav/repairer-invoices/${invoiceId}/mark-paid`,
      { paymentDate, paymentReference },
    );
    return data;
  },

  /**
   * Télécharge la facture réparateur (document comptable du vendeur). Le
   * réparateur peut l'avoir déposée en PDF, JPEG ou PNG : l'extension vient
   * du serveur, qui détecte le type réel. `fileName` (sans extension) sert de
   * partie lisible, utile tant qu'une ancienne API renvoie un nom uuid.
   */
  downloadDocument: async (invoiceId: string, fileName?: string): Promise<void> => {
    const response = await axiosInstance.get<Blob>(
      `/pro-v2/sav/repairer-invoices/${invoiceId}/document`,
      { responseType: 'blob' },
    );
    // Garde-fou : une erreur API sérialisée JSON ne doit pas être
    // sauvegardée comme un document corrompu.
    if (response.data.type?.includes('application/json')) {
      throw new Error('Document indisponible');
    }
    downloadBlobResponse(response, fileName);
  },
};
